import React, { useState } from 'react';
import { K, HOSE as DEFAULT_HOSE } from '../constants';
import { calcFL, uid } from '../utils/formulas';
import { NI, Sel, Lbl, SecHd, AddBtn, MathBox } from '../components/ui';
import { ArrowRight, ArrowUp, ArrowDown, CheckCircle2, XCircle, X } from 'lucide-react';

export function RelayPump({ showMath, calcDefaults }) {
  const HOSE = calcDefaults?.hose || DEFAULT_HOSE;
  const MIN_INLET = calcDefaults?.ops?.minInlet ?? 20;
  const defPSI = calcDefaults?.ops?.defaultPumpPSI ?? 150;
  const defGPM = calcDefaults?.ops?.defaultPumpGPM ?? 1500;
  const elevFactor = calcDefaults?.ops?.elevFactor ?? 0.5;

  const [pumpers, setPumpers] = useState([
    { id: 1, name: 'Source Pumper',  pump: defGPM, maxPSI: defPSI, hose: '5"', len: 1000, gpm: 500, elv: 0 },
    { id: 2, name: 'Attack Pumper',  pump: defGPM, maxPSI: defPSI, hose: '5"', len: 0,    gpm: 500, elv: 0 },
  ]);

  const updateP = (id, field, value) => setPumpers((p) => p.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
  const addP = () => {
    const id = uid();
    setPumpers((p) => {
      const last = p[p.length - 1];
      const inter = { id, name: `Intermediate ${p.length}`, pump: defGPM, maxPSI: defPSI, hose: '5"', len: 1000, gpm: 500, elv: 0 };
      return [...p.slice(0, -1), inter, last];
    });
  };
  const removeP = (id) => setPumpers((p) => (p.length > 2 ? p.filter((x) => x.id !== id) : p));

  const stages = pumpers.map((p, i) => {
    const segFL = p.len > 0 ? calcFL(HOSE[p.hose].c, p.gpm, p.len) : 0;
    const segElv = elevFactor * (p.elv || 0);
    const isLast = i === pumpers.length - 1;
    const reqPDP = isLast ? 0 : segFL + segElv + MIN_INLET;
    const ok = isLast ? true : reqPDP <= p.maxPSI;
    return { p, segFL, segElv, reqPDP, isLast, isFirst: i === 0, ok };
  });

  const allOk = stages.every((s) => s.ok);

  return (
    <div>
      <SecHd title="Relay Pumping" sub="Multi-stage relay with source, intermediate, and attack pumper logic." />

      <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 0, marginBottom: 20, padding: '4px 0' }}>
        {stages.map((s) => (
          <div key={s.p.id} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ background: s.isLast ? `${K.red}22` : `${K.ok}15`, border: `1px solid ${s.ok ? (s.isLast ? K.red : K.ok) : K.bad}`, borderRadius: 9, padding: '8px 10px', minWidth: 92, textAlign: 'center' }}>
              <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2, marginBottom: 4, letterSpacing: 0.3 }}>{s.isFirst ? 'Source' : s.isLast ? 'Attack' : 'Inter'}</div>
              <div style={{ fontFamily: K.fSans, fontSize: 15, fontWeight: 600 }}>{s.p.name}</div>
              {!s.isLast ? <div style={{ fontFamily: K.fMono, fontSize: 18, fontWeight: 500, color: s.ok ? K.ok : K.bad, marginTop: 3 }}>{s.reqPDP.toFixed(0)}<span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}> psi</span></div> : <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.red, marginTop: 4 }}>Attack engine</div>}
            </div>
            {!s.isLast ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 6px' }}>
                <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}>{s.p.hose} × {s.p.len}ft</div>
                <div style={{ display: 'flex', alignItems: 'center', color: K.t2 }}><ArrowRight size={18} color={K.t2} strokeWidth={1.75} /></div>
                <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}>{s.segFL.toFixed(0)} psi FL</div>
                {s.segElv !== 0 ? <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontFamily: K.fSans, fontSize: 11, color: s.segElv > 0 ? K.bad : K.ok }}>{s.segElv > 0 ? <ArrowUp size={12} color={K.bad} strokeWidth={1.75} /> : <ArrowDown size={12} color={K.ok} strokeWidth={1.75} />} {Math.abs(s.segElv).toFixed(0)} psi</div> : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ background: allOk ? `${K.ok}22` : `${K.bad}22`, border: `1px solid ${allOk ? K.ok : K.bad}`, borderRadius: 10, padding: '10px 14px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: K.fSans, fontSize: 20, fontWeight: 600, color: allOk ? K.ok : K.bad }}>{allOk ? <CheckCircle2 size={20} color={K.ok} strokeWidth={1.75} /> : <XCircle size={20} color={K.bad} strokeWidth={1.75} />}{allOk ? 'Relay viable' : 'Relay problem'}</div>
        <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t1, marginTop: 4 }}>{allOk ? 'All pumping stages are within pressure limits.' : 'At least one stage exceeds the assigned pumper pressure limit.'}</div>
      </div>

      {stages.map((s) => (
        <div key={s.p.id} style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderLeft: `3px solid ${s.isFirst ? K.info : s.isLast ? K.red : K.ok}`, borderRadius: 12, padding: 14, marginBottom: 12, boxShadow: K.sh1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <input value={s.p.name} onChange={(e) => updateP(s.p.id, 'name', e.target.value)} style={{ background: 'transparent', border: 'none', color: K.t0, fontFamily: K.fSans, fontSize: 14, flex: 1, marginRight: 8, fontWeight: 600 }} />
            {!s.isFirst && !s.isLast && pumpers.length > 2 ? <button onClick={() => removeP(s.p.id)} style={{ background: 'none', border: 'none', color: K.t2, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}><X size={18} color={K.t2} strokeWidth={1.75} /></button> : null}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><Lbl t="Pump Capacity (GPM)" /><NI v={s.p.pump} set={(v) => updateP(s.p.id, 'pump', v)} min={500} max={3000} /></div>
            <div><Lbl t="Max PSI" /><NI v={s.p.maxPSI} set={(v) => updateP(s.p.id, 'maxPSI', v)} min={50} max={400} /></div>
            {!s.isLast ? (
              <>
                <div><Lbl t="Hose Size" /><Sel v={s.p.hose} set={(v) => updateP(s.p.id, 'hose', v)}>{Object.keys(HOSE).map((d) => <option key={d}>{d}</option>)}</Sel></div>
                <div><Lbl t="Hose Length (ft)" /><NI v={s.p.len} set={(v) => updateP(s.p.id, 'len', v)} min={0} max={10000} /></div>
                <div><Lbl t="Elevation Change (ft)" /><NI v={s.p.elv} set={(v) => updateP(s.p.id, 'elv', v)} min={-2000} max={2000} /></div>
                <div><Lbl t="Flow (GPM)" /><NI v={s.p.gpm} set={(v) => updateP(s.p.id, 'gpm', v)} min={100} max={2000} /></div>
              </>
            ) : null}
          </div>
          {!s.isLast ? <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}><span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}>Segment FL: <span style={{ fontFamily: K.fMono, color: K.t0, fontWeight: 500 }}>{s.segFL.toFixed(1)} psi</span></span><span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}>Required PDP: <span style={{ fontFamily: K.fMono, color: s.ok ? K.ok : K.bad, fontWeight: 500 }}>{s.reqPDP.toFixed(1)} psi</span></span></div> : null}
        </div>
      ))}

      {pumpers.length < 4 ? <AddBtn col={K.red} onClick={addP}>+ Add Intermediate Pumper</AddBtn> : null}

      <MathBox show={showMath} steps={[
        { l: 'Relay principle', f: 'Each pumper pushes through its hose segment and the next pumper boosts again' },
        { l: 'Min inlet pressure', f: `${MIN_INLET} psi at each pumper intake` },
        ...stages.filter((s) => !s.isLast).map((s) => ({ l: s.p.name, f: `FL ${s.segFL.toFixed(1)} + Elev ${s.segElv.toFixed(1)} + ${MIN_INLET}`, r: `${s.reqPDP.toFixed(1)} psi PDP` })),
      ]} />
    </div>
  );
}
